"use client"

import { useState, useEffect } from "react"
import { useTranslations } from "next-intl"
import { useAuthStore } from "@/lib/auth-store"
import { createClient } from "@/lib/supabase/client"
import { X, Mail, Loader2, CheckCircle2 } from "@/lib/icons"
import { Button } from "@/components/ui/button"

export function EmailVerificationBanner() {
  const t = useTranslations("emailVerification")
  const tCommon = useTranslations("common")
  const { user } = useAuthStore()
  const [confirmed, setConfirmed] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)
  const [showDetails, setShowDetails] = useState(false)

  // Listen for email confirmation in real time (user clicks link in another tab)
  useEffect(() => {
    if (!user) return
    const supabase = createClient()
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event: any, session: any) => {
      if ((event === "USER_UPDATED" || event === "SIGNED_IN") && session?.user?.email_confirmed_at) {
        setConfirmed(true)
      }
    })
    return () => subscription.unsubscribe()
  }, [user?.id])

  // Don't show for: logged out, OAuth users (already verified), confirmed, dismissed
  const isOAuth = user?.app_metadata?.provider && user.app_metadata.provider !== "email"
  const isConfirmed = confirmed || !!user?.email_confirmed_at
  if (!user || isOAuth || isConfirmed || dismissed) return null

  const handleResendEmail = async () => {
    setResending(true)
    try {
      const supabase = createClient()
      const { error } = await supabase.auth.resend({ type: "signup", email: user.email! })
      if (error) throw error
      setResent(true)
      setTimeout(() => setResent(false), 5000)
    } catch (err) {
      console.error("Failed to resend email:", err)
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border-b border-orange-500/20">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <Mail className="h-5 w-5 text-orange-500 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">
                {t("title")}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("checkInbox")}{" "}
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="underline hover:text-foreground transition-colors"
                >
                  {showDetails ? t("hideRestricted") : t("showRestricted")}
                </button>
              </p>

              {showDetails && (
                <div className="mt-2 text-xs text-muted-foreground bg-background/50 rounded-lg p-2 border border-border/50">
                  <p className="font-medium mb-1">{t("untilYouVerify")}</p>
                  <ul className="list-disc list-inside space-y-0.5 ml-1">
                    <li>{t("restrictedScripts")}</li>
                    <li>{t("restrictedUpload")}</li>
                    <li>{t("restrictedComment")}</li>
                    <li>{t("restrictedMessages")}</li>
                    <li>{t("restrictedCompanions")}</li>
                    <li>{t("restrictedCommission")}</li>
                  </ul>
                  <p className="mt-2 text-green-600 font-medium">{t("canStillBrowse")}</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 shrink-0">
            {resent ? (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                <span>{t("emailSent")}</span>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleResendEmail}
                disabled={resending}
                className="text-xs"
              >
                {resending ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    {t("sending")}
                  </>
                ) : (
                  t("resendEmail")
                )}
              </Button>
            )}
            
            <button
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
              aria-label={tCommon("dismiss")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
